<div class="wrapper" [class.wrapper-investor]="isInvestorOrganization$ | async"
  [class.wrapper-investor-compact]="(isInvestorOrganization$ | async) && (data?.skipToInviteStep || data?.fromProductCreationHeaderNav)">
  <div class="dialog-header" fxLayout="row" fxLayoutAlign="space-between center">
    <h1 mat-dialog-title class="left-aligned-title">{{ dialogTitleKey | translate }}</h1>
    <button mat-icon-button class="close-button" (click)="close()" aria-label="Close dialog" type="button">
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <div mat-dialog-content fxLayout="column" fxLayoutGap="24px" class="single-screen-content">
    <form fxLayout="column" [formGroup]="form" fxLayoutGap="24px">
      <!-- Investor Organization: Flow Selection, Issuer Search, Fund Search, and Issuer Admin Lookup -->
      <div class="investor-organization-container" *ngIf="isInvestorOrganization$ | async">
        <!-- Full-width flow cards (investor org) -->
        <div class="form-section flow-selection-section"
          *ngIf="!data?.fromProductCreationHeaderNav && !data?.skipToInviteStep">
          <p class="flow-selection-hint">{{ 'INTL_FLOW_SELECTION_HINT' | translate }}</p>
          <div class="flow-selection-grid">
            <button type="button" class="flow-option flow-option-card"
              [class.selected]="form.get('flowType')?.value === 'invite_issuer'"
              [attr.aria-pressed]="form.get('flowType')?.value === 'invite_issuer'"
              (click)="onFlowTypeSelected('invite_issuer')">
              <div class="flow-option-card__header">
                <span class="flow-option-card__icon-wrap" aria-hidden="true">
                  <mat-icon class="flow-option-card__icon">forward_to_inbox</mat-icon>
                </span>
                <mat-icon *ngIf="form.get('flowType')?.value === 'invite_issuer'" class="flow-option-card__badge"
                  color="primary">check_circle</mat-icon>
              </div>
              <div class="flow-option-card__body">
                <span class="radio-label">{{ 'INTL_INVITE_ISSUER_TO_CREATE_FUND' | translate }}</span>
                <span class="radio-description">{{ 'INTL_INVITE_ISSUER_DESCRIPTION' | translate }}</span>
              </div>
            </button>
            <button type="button" class="flow-option flow-option-card"
              [class.selected]="form.get('flowType')?.value === 'create_fund_later'"
              [attr.aria-pressed]="form.get('flowType')?.value === 'create_fund_later'"
              (click)="onFlowTypeSelected('create_fund_later')">
              <div class="flow-option-card__header">
                <span class="flow-option-card__icon-wrap" aria-hidden="true">
                  <mat-icon class="flow-option-card__icon">event_available</mat-icon>
                </span>
                <mat-icon *ngIf="form.get('flowType')?.value === 'create_fund_later'" class="flow-option-card__badge"
                  color="primary">check_circle</mat-icon>
              </div>
              <div class="flow-option-card__body">
                <span class="radio-label">{{ 'INTL_CREATE_FUND_AND_INVITE_LATER' | translate }}</span>
                <span class="radio-description">{{ 'INTL_CREATE_FUND_LATER_DESCRIPTION' | translate }}</span>
              </div>
            </button>
            <button type="button" class="flow-option flow-option-card"
              [class.selected]="form.get('flowType')?.value === 'invite_issuer_direct_deal'"
              [attr.aria-pressed]="form.get('flowType')?.value === 'invite_issuer_direct_deal'"
              (click)="onFlowTypeSelected('invite_issuer_direct_deal')">
              <div class="flow-option-card__header">
                <span class="flow-option-card__icon-wrap" aria-hidden="true">
                  <mat-icon class="flow-option-card__icon">handshake</mat-icon>
                </span>
                <mat-icon *ngIf="form.get('flowType')?.value === 'invite_issuer_direct_deal'"
                  class="flow-option-card__badge" color="primary">check_circle</mat-icon>
              </div>
              <div class="flow-option-card__body">
                <span class="radio-label">{{ 'INTL_INVITE_ISSUER_TO_CREATE_DIRECT_DEAL' | translate }}</span>
                <span class="radio-description">{{ 'INTL_INVITE_ISSUER_DIRECT_DEAL_DESCRIPTION' | translate }}</span>
              </div>
            </button>
            <button type="button" class="flow-option flow-option-card"
              [class.selected]="form.get('flowType')?.value === 'create_direct_deal_later'"
              [attr.aria-pressed]="form.get('flowType')?.value === 'create_direct_deal_later'"
              (click)="onFlowTypeSelected('create_direct_deal_later')">
              <div class="flow-option-card__header">
                <span class="flow-option-card__icon-wrap" aria-hidden="true">
                  <mat-icon class="flow-option-card__icon">business_center</mat-icon>
                </span>
                <mat-icon *ngIf="form.get('flowType')?.value === 'create_direct_deal_later'"
                  class="flow-option-card__badge" color="primary">check_circle</mat-icon>
              </div>
              <div class="flow-option-card__body">
                <span class="radio-label">{{ 'INTL_CREATE_DIRECT_DEAL_AND_INVITE_LATER' | translate }}</span>
                <span class="radio-description">{{ 'INTL_CREATE_DIRECT_DEAL_LATER_DESCRIPTION' | translate }}</span>
              </div>
            </button>
          </div>
        </div>

        <div class="investor-main-column" fxLayout="column" fxLayoutGap="0">
          <!-- Issuer Search - Visible when flow is selected -->
          <div class="form-section" fxLayout="column" fxLayoutGap="12px" *ngIf="form.get('flowType')?.value">
            <h3 class="section-title">{{ 'INTL_ISSUER_COMPANY_NAME' | translate }} *</h3>
            <wai-form-autocomplete appearance="outline" label="{{ 'INTL_ISSUER_COMPANY_NAME' | translate }}"
              [options$]="issuerOptions$" [control]="asFormControl(form.controls.issuerSearch)"
              [displayFn]="displayIssuerFn" [clearable]="true" (optionSelected)="onIssuerSelected($event)"
              placeholder="{{ 'INTL_TYPE_TO_SEARCH_ISSUER' | translate }}"></wai-form-autocomplete>
          </div>

          <!-- Fund Search - Visible when issuer is filled -->
          <div class="form-section" fxLayout="column" fxLayoutGap="12px"
            *ngIf="form.get('flowType')?.value && (form.get('issuerUid')?.value || form.get('issuerName')?.value)">
            <h3 class="section-title">{{ 'INTL_FUND_NAME' | translate }} *</h3>
            <wai-form-autocomplete appearance="outline" label="{{ 'INTL_FUND_NAME' | translate }}"
              [options$]="fundOptions$" [control]="asFormControl(form.controls.fundSearch)" [displayFn]="displayFundFn"
              [clearable]="true" (optionSelected)="onFundSelected($event)"
              placeholder="{{ 'INTL_TYPE_TO_SEARCH_FUND' | translate }}"></wai-form-autocomplete>

            <div *ngIf="showFundTypeFields" fxLayout="column" fxLayoutGap="16px" class="fund-type-section">
              <wai-form-checkbox *ngIf="isDirectDealAssetClass()" label="{{ 'INTL_KEEP_PRIVATE' | translate }}"
                labelPosition="after" [control]="asFormControl(form.controls.keepPrivate)">
              </wai-form-checkbox>

              <div fxLayout="row" fxLayoutGap="16px">
                <wai-form-dropdown fxFlex appearance="outline" label="{{ 'INTL_FUND_TYPE' | translate }}"
                  [options]="assetClasses$ | async" [control]="asFormControl(form.controls.fundType)"
                  [isDisabled]="isDirectDealFlow(form.get('flowType')?.value)"
                  [updateOutline]="true">
                </wai-form-dropdown>

                <wai-form-dropdown fxFlex appearance="outline" label="{{ 'INTL_FUND_SUBTYPE' | translate }}"
                  [options]="assetSubclasses$ | async" [control]="asFormControl(form.controls.fundSubtype)"
                  [isDisabled]="!form.controls.fundType?.value" [updateOutline]="true">
                </wai-form-dropdown>
              </div>
            </div>

            <div
              *ngIf="isInviteFlow(form.get('flowType')?.value) && (isPreparingInviteAdminFund || isLoadingIssuerAdmin) && !form.get('productUid')?.value"
              class="investor-invite-spinner-row" fxLayout="row" fxLayoutGap="8px" fxLayoutAlign="start center">
              <mat-spinner diameter="20"></mat-spinner>
            </div>
          </div>

          <!-- Issuer Admin Lookup & Invite - invite flows only -->
          <div class="form-section investor-invite-container" fxLayout="column" fxLayoutGap="16px"
            *ngIf="isInviteFlow(form.get('flowType')?.value) && form.get('issuerUid')?.value && form.get('productUid')?.value">
            <h3 class="section-title">{{ 'INTL_INVITE_ISSUER_ADMIN' | translate }} *</h3>

            <div class="issuer-info" fxLayout="row" fxLayoutGap="8px" fxLayoutAlign="start center"
              *ngIf="form.get('issuerName')?.value">
              <mat-icon>business</mat-icon>
              <span>{{ form.get('issuerName')?.value }}</span>
            </div>

            <ng-container *ngIf="form.get('productUid')?.value">
              <wai-form-autocomplete appearance="outline" label="{{ 'INTL_EMAIL' | translate }}"
                [options$]="issuerAdminEmailOptions$" [control]="asFormControl(form.controls.issuerAdminEmail)"
                [displayFn]="displayIssuerAdminEmailFn" [clearable]="true"
                (optionSelected)="onIssuerAdminEmailSelected($event)"
                placeholder="{{ 'INTL_ENTER_ISSUER_ADMIN_EMAIL' | translate }}"></wai-form-autocomplete>

              <div *ngIf="isLoadingIssuerAdmin" class="investor-invite-spinner-row" fxLayout="row" fxLayoutGap="8px"
                fxLayoutAlign="start center">
                <mat-spinner diameter="20"></mat-spinner>
              </div>
            </ng-container>

            <div *ngIf="issuerAdminResult" class="issuer-admin-result" fxLayout="column" fxLayoutGap="8px">
              <mat-card>
                <mat-card-content fxLayout="column" fxLayoutGap="8px">
                  <div fxLayout="row" fxLayoutAlign="space-between center">
                    <span class="result-label">{{ 'INTL_ISSUER_ADMIN_FOUND' | translate }}</span>
                    <mat-icon color="primary">check_circle</mat-icon>
                  </div>
                  <div fxLayout="column" fxLayoutGap="4px">
                    <div><strong>{{ 'INTL_NAME' | translate }}:</strong> {{ issuerAdminResult.userFullName }}</div>
                    <div><strong>{{ 'INTL_EMAIL' | translate }}:</strong> {{ issuerAdminResult.email }}</div>
                    <div><strong>{{ 'INTL_COMPANY' | translate }}:</strong> {{ issuerAdminResult.issuerCompanyName }}
                    </div>
                  </div>
                </mat-card-content>
              </mat-card>
            </div>

            <div *ngIf="showInvestorInviteWarning" class="warning-message" fxLayout="row" fxLayoutAlign="start center"
              fxLayoutGap="8px">
              <mat-icon color="warn">warning</mat-icon>
              <span class="warning-text">{{ 'INTL_ADMIN_DOES_NOT_EXIST_INVITE_NOW' | translate }}</span>
            </div>

            <div *ngIf="showInvestorInviteWarning" class="new-investor-fields" fxLayout="column" fxLayoutGap="16px">
              <h3 class="section-title">New Issuer Information</h3>

              <div fxLayout="row" fxLayoutGap="16px">
                <wai-form-dropdown fxFlex="160px" appearance="outline" label="{{ 'INTL_SALUTATION' | translate }}"
                  [options]="salutations$ | async" [control]="asFormControl(form.controls.newInvestorSalutation)"
                  [isDisabled]="false"
                  [updateOutline]="true"></wai-form-dropdown>

                <wai-form-input fxFlex appearance="outline" label="{{ 'INTL_FIRST_NAME' | translate }}"
                  [control]="asFormControl(form.controls.newInvestorFirstName)" [updateOutline]="true"
                  placeholder="{{ 'INTL_ENTER_FIRST_NAME' | translate }}"></wai-form-input>
              </div>

              <wai-form-input appearance="outline" label="{{ 'INTL_LAST_NAME' | translate }}"
                [control]="asFormControl(form.controls.newInvestorLastName)" [updateOutline]="true"
                placeholder="{{ 'INTL_ENTER_LAST_NAME' | translate }}"></wai-form-input>
            </div>
          </div>
        </div>
      </div>

      <!-- GP (Emittent) Invite Container - Only for emittent organization type -->
      <div class="form-section gp-invite-container" fxLayout="column" fxLayoutGap="16px"
        *ngIf="(isEmittentOrganization$ | async)">
        <!-- Email Field -->
        <wai-form-input appearance="outline" label="{{ 'INTL_EMAIL' | translate }}"
          [control]="asFormControl(form.controls.issuerAdminEmail)" [updateOutline]="true" type="email"
          placeholder="{{ 'INTL_ENTER_INVITEE_EMAIL' | translate }}"></wai-form-input>

        <button mat-button color="primary" (click)="searchIssuerAdmin()"
          [disabled]="!form.get('issuerAdminEmail')?.valid || isLoadingIssuerAdmin" fxLayout="row"
          fxLayoutAlign="center center" fxLayoutGap="8px">
          <mat-icon *ngIf="!isLoadingIssuerAdmin">search</mat-icon>
          <mat-spinner *ngIf="isLoadingIssuerAdmin" diameter="20"></mat-spinner>
          <span>{{ 'INTL_SEARCH' | translate }}</span>
        </button>

        <!-- Issuer Admin Result (Existing Investor) -->
        <div *ngIf="issuerAdminResult && !isNewInvestor" class="issuer-admin-result" fxLayout="column"
          fxLayoutGap="8px">
          <mat-card>
            <mat-card-content fxLayout="column" fxLayoutGap="8px">
              <div fxLayout="row" fxLayoutAlign="space-between center">
                <span class="result-label">{{ 'INTL_LP_INFORMATION_FOUND' | translate }}</span>
                <mat-icon color="primary">check_circle</mat-icon>
              </div>
              <div fxLayout="column" fxLayoutGap="4px">
                <div *ngIf="issuerAdminResult.userFullName">
                  <strong>{{ 'INTL_NAME' | translate }}:</strong> {{ issuerAdminResult.userFullName }}
                </div>
                <div><strong>{{ 'INTL_EMAIL' | translate }}:</strong> {{ issuerAdminResult.email }}</div>
                <div *ngIf="issuerAdminResult.fullCompanyName">
                  <strong>{{ 'INTL_COMPANY_NAME' | translate }}:</strong> {{ issuerAdminResult.fullCompanyName }}
                </div>
                <div *ngIf="issuerAdminResult.investorType">
                  <strong>{{ 'INTL_INVESTOR_TYPE' | translate }}:</strong> {{ issuerAdminResult.investorType }}
                </div>
              </div>
            </mat-card-content>
          </mat-card>
        </div>

        <!-- Warning Message (shown when exists === false) -->
        <div *ngIf="isNewInvestor" class="warning-message" fxLayout="row" fxLayoutAlign="start center"
          fxLayoutGap="8px">
          <mat-icon color="warn">warning</mat-icon>
          <span class="warning-text">{{ 'INTL_EMAIL_DOES_NOT_EXIST_ENTER_DETAILS_TO_INVITE' | translate }}</span>
        </div>

        <!-- New Investor Fields (shown when exists === false) -->
        <div *ngIf="isNewInvestor" class="new-investor-fields" fxLayout="column" fxLayoutGap="16px">
          <h3 class="section-title">{{ 'INTL_NEW_INVESTOR_INFORMATION' | translate }}</h3>

          <div fxLayout="row" fxLayoutGap="16px">
            <wai-form-dropdown fxFlex="160px" appearance="outline" label="{{ 'INTL_SALUTATION' | translate }}"
              [options]="salutations$ | async" [control]="asFormControl(form.controls.newInvestorSalutation)"
              [isDisabled]="false"
              [updateOutline]="true"></wai-form-dropdown>

            <wai-form-input fxFlex appearance="outline" label="{{ 'INTL_FIRST_NAME' | translate }}"
              [control]="asFormControl(form.controls.newInvestorFirstName)" [updateOutline]="true"
              placeholder="{{ 'INTL_ENTER_FIRST_NAME' | translate }}"></wai-form-input>
          </div>

          <!-- Last Name -->
          <wai-form-input appearance="outline" label="{{ 'INTL_LAST_NAME' | translate }}"
            [control]="asFormControl(form.controls.newInvestorLastName)" [updateOutline]="true"
            placeholder="{{ 'INTL_ENTER_LAST_NAME' | translate }}"></wai-form-input>

          <!-- User Type Radio Group -->
          <div class="form-section" [class.no-border-bottom]="form.get('newInvestorUserType')?.value === 'company'"
            fxLayout="column" fxLayoutGap="12px">
            <h3 class="section-title">{{ 'INTL_USER_TYPE' | translate }} *</h3>
            <mat-radio-group [formControl]="asFormControl(form.controls.newInvestorUserType)" fxLayout="row"
              fxLayoutGap="24px">
              <mat-radio-button value="natural_person">
                <span class="radio-label">{{ 'INTL_NATURAL_PERSON' | translate }}</span>
              </mat-radio-button>
              <mat-radio-button value="company">
                <span class="radio-label">{{ 'INTL_COMPANY' | translate }}</span>
              </mat-radio-button>
            </mat-radio-group>
          </div>

          <!-- Investor Company Name (shown only when company is selected) -->
          <wai-form-input *ngIf="form.get('newInvestorUserType')?.value === 'company'"
            label="{{ 'INTL_INVESTOR_COMPANY_NAME' | translate }}"
            [control]="asFormControl(form.controls.newInvestorCompanyName)" [updateOutline]="true"
            placeholder="{{ 'INTL_ENTER_INVESTOR_COMPANY_NAME' | translate }}"></wai-form-input>
        </div>

      </div>
    </form>
  </div>

  <div mat-dialog-actions class="dialog-footer" fxLayoutAlign="space-between center" fxLayoutGap="8px">
    <div fxLayout="row" fxLayoutGap="8px">
      <button fxLayout="row" mat-button (click)="close()">
        <mat-icon>block</mat-icon>
        <span>&nbsp;{{ 'INTL_CANCEL' | translate }}</span>
      </button>
    </div>

    <div fxLayout="row" fxLayoutGap="8px">
      <!-- Create Fund button - for create flows (investor organizations only) -->
      <button *ngIf="(isInvestorOrganization$ | async) && isCreateFlow(form.get('flowType')?.value)"
        type="submit" mat-button color="accent" (click)="create()"
        [disabled]="!isFormReadyForCreate() || form.invalid || isCreating || isLoadingIssuerAdmin || isPreparingInviteAdminFund"
        fxLayout="row" fxLayoutAlign="center center"
        fxLayoutGap="8px">
        <mat-spinner *ngIf="isCreating" diameter="16"></mat-spinner>
        <span>{{ 'INTL_CREATE' | translate }}</span>
      </button>

      <!-- Invite button - for invite flows (investor organizations) -->
      <button *ngIf="(isInvestorOrganization$ | async) && isInviteFlow(form.get('flowType')?.value)" mat-button
        color="accent" (click)="inviteIssuerAdmin()" [disabled]="!isFormReadyForInvite() || form.invalid">
        {{ 'INTL_INVITE' | translate }}
      </button>

      <!-- Invite button - for emittent organizations -->
      <button *ngIf="isEmittentOrganization$ | async" mat-button color="accent" (click)="inviteIssuerAdmin()"
        [disabled]="!isFormReadyForInvite()">
        {{ 'INTL_INVITE' | translate }}
      </button>
    </div>
  </div>
</div>
